import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Mail, MessageCircle, MapPin, Clock, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS, RESTAURANT_CONSTANTS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Accueil', path: ROUTE_PATHS.HOME },
    { name: 'Menu', path: ROUTE_PATHS.MENU },
    { name: 'À propos', path: ROUTE_PATHS.ABOUT },
    { name: 'Instagram', path: ROUTE_PATHS.INSTAGRAM },
    { name: 'Contact', path: ROUTE_PATHS.CONTACT },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Navigation Header */}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
          isScrolled 
            ? "bg-background/90 backdrop-blur-md py-3 border-border shadow-sm" 
            : "bg-transparent py-5 border-transparent"
        )}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link 
            to={ROUTE_PATHS.HOME} 
            className="flex flex-col items-start leading-tight group"
          >
            <span className="text-xl md:text-2xl font-bold text-primary tracking-tight transition-colors">
              Blessing
            </span>
            <span className="text-[10px] md:text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Service-Pépinière
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-semibold transition-all hover:text-accent relative py-1",
                    isActive ? "text-accent" : "text-foreground/80"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {item.name}
                    {isActive && (
                      <motion.div
                        layoutId="nav-underline"
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Action Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button 
              asChild 
              variant="outline" 
              size="sm" 
              className="rounded-full border-primary/20 hover:bg-primary/5"
            >
              <a href={`mailto:${RESTAURANT_CONSTANTS.EMAIL}`}>
                <Mail className="w-4 h-4 mr-2" />
                Email
              </a>
            </Button>
            <Button 
              asChild 
              size="sm" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full font-bold shadow-lg shadow-accent/20"
            >
              <a 
                href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-4 h-4 mr-2 fill-current" />
                Commander
              </a>
            </Button>
          </div>

          {/* Mobile Menu Trigger */}
          <Sheet>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-primary">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l-border">
              <SheetHeader className="mb-8 text-left">
                <SheetTitle className="text-2xl font-bold text-primary">
                  Blessing
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center justify-between p-4 rounded-xl text-lg font-medium transition-colors",
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-muted text-foreground"
                      )
                    }
                  >
                    {item.name}
                    <ChevronRight className="h-5 w-5 opacity-50" />
                  </NavLink>
                ))}
                <div className="grid grid-cols-1 gap-3 mt-8">
                  <Button asChild className="bg-accent hover:bg-accent/90 h-14 text-lg font-bold">
                    <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Commander sur WhatsApp
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="h-14 text-lg font-bold border-primary/20">
                    <a href={RESTAURANT_CONSTANTS.INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer">
                      <Instagram className="w-5 h-5 mr-2 text-primary" />
                      Instagram
                    </a>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground pt-16 pb-8 border-t border-primary-foreground/10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* Brand Section */}
            <div className="space-y-6">
              <div className="flex flex-col">
                <span className="text-2xl font-bold tracking-tight">Blessing</span>
                <span className="text-xs font-medium uppercase tracking-[0.2em] opacity-70">Service-Pépinière</span>
              </div>
              <p className="text-primary-foreground/70 max-w-xs leading-relaxed">
                Le goût authentique du fait maison. Un voyage culinaire entre l'Afrique et l'Europe, préparé avec passion.
              </p>
              <div className="flex gap-4">
                <a 
                  href={RESTAURANT_CONSTANTS.INSTAGRAM_LINK} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-all"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href={`mailto:${RESTAURANT_CONSTANTS.EMAIL}`} 
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-all"
                >
                  <Mail className="w-5 h-5" />
                </a>
                <a 
                  href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-all"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-primary-foreground/80">
                  <MapPin className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>{RESTAURANT_CONSTANTS.ADDRESS}</span>
                </li>
                <li className="flex items-start gap-3 text-primary-foreground/80">
                  <Mail className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <a href={`mailto:${RESTAURANT_CONSTANTS.EMAIL}`} className="hover:text-accent transition-colors">
                    {RESTAURANT_CONSTANTS.EMAIL}
                  </a>
                </li>
                <li className="flex items-start gap-3 text-primary-foreground/80">
                  <Clock className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>{RESTAURANT_CONSTANTS.HOURS}</span>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold">Navigation</h4>
              <ul className="grid grid-cols-2 gap-4">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link 
                      to={item.path} 
                      className="text-primary-foreground/80 hover:text-accent transition-colors flex items-center gap-2"
                    >
                      <ChevronRight className="w-3 h-3 text-accent" />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="pt-4">
                <Button asChild className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold">
                  <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    Commander maintenant
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="border-t border-primary-foreground/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-primary-foreground/50">
              © 2026 {RESTAURANT_CONSTANTS.NAME}. Tous droits réservés.
            </p>
            <p className="text-sm text-primary-foreground/50">
              Dirigé par {RESTAURANT_CONSTANTS.OWNER}
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Action (Mobile Only) */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <motion.a
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          href={RESTAURANT_CONSTANTS.WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 bg-accent text-accent-foreground rounded-full shadow-2xl"
        >
          <MessageCircle className="w-7 h-7" />
        </motion.a>
      </div>
    </div>
  );
}
